import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebaseFunctions';
import {
  PDF_COLOR_TEXT,
  PDF_COLOR_TEXT_SECONDARY,
  PDF_FOOTER_BRAND_TEXT,
  PDF_FOOTER_BRAND_URL,
  PDF_FOOTER_PADDING,
  PDF_FONT_SIZE_FOOTER,
  PDF_FONT_SIZE_HEADER,
  PDF_HEADER_PADDING,
  PDF_MARGIN_BOTTOM,
  PDF_MARGIN_LEFT,
  PDF_MARGIN_RIGHT,
  PDF_PAGE_FORMAT,
  PDF_PAGE_LANDSCAPE,
} from '#types/System.Themes.types';
import { LOGO_EXPORT_SIZE_DEFAULT } from '#types/System.Parameters.types';

interface PdfGenerationResponse {
    success: boolean;
    pdf: string;
    contentType: string;
}

interface ServerPdfParams {
    contentRef: React.RefObject<HTMLDivElement | null>;
    headerLogoDataUrl?: string;
    headerLogoSize?: string;
    headerRightText?: string;
    footerLeftText?: string | string[];
}

const PDF_CAPTURE_WAIT_TIMEOUT_MS = 10000;
const PDF_CAPTURE_POLL_MS = 100;
const PDF_CAPTURE_STABLE_PASSES = 2;
const PDF_LOADING_SELECTOR = [
    '[aria-label="artable-aggregation-loading"]',
    '.ant-spin-spinning',
    '.ant-btn-loading',
    '.ant-tree-switcher-loading-icon',
].join(', ');

const waitForNextFrame = async (): Promise<void> => {
    await new Promise<void>((resolve) => {
        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => resolve());
            });
            return;
        }

        setTimeout(resolve, 0);
    });
};

const hasPendingPdfLoaders = (root: HTMLDivElement): boolean => {
    const hasLoaderElement = root.querySelector(PDF_LOADING_SELECTOR) !== null;
    const hasTableLoadingText = Array
        .from(root.querySelectorAll<HTMLElement>('[aria-label="artable-body"]'))
        .some((tableRoot) => tableRoot.textContent?.includes('Loading...'));

    return hasLoaderElement || hasTableLoadingText;
};

const describePendingPdfLoaders = (root: HTMLDivElement): string => {
    const pendingLabels = Array
        .from(root.querySelectorAll<HTMLElement>(PDF_LOADING_SELECTOR))
        .slice(0, 5)
        .map((element) => {
            const ariaLabel = element.getAttribute('aria-label');
            if (ariaLabel) {
                return ariaLabel;
            }

            if (typeof element.className === 'string' && element.className.trim()) {
                return element.className.trim();
            }

            return element.tagName.toLowerCase();
        });

    const hasTableLoadingText = Array
        .from(root.querySelectorAll<HTMLElement>('[aria-label="artable-body"]'))
        .some((tableRoot) => tableRoot.textContent?.includes('Loading...'));

    if (hasTableLoadingText) {
        pendingLabels.push('artable-body:Loading...');
    }

    return pendingLabels.join(', ');
};

const waitForPdfContentToSettle = async (
    contentRef: React.RefObject<HTMLDivElement | null>,
): Promise<HTMLDivElement> => {
    let stablePasses = 0;
    const startedAt = Date.now();

    while (Date.now() - startedAt < PDF_CAPTURE_WAIT_TIMEOUT_MS) {
        const root = contentRef.current;
        if (!root) {
            throw new Error('Content reference is null');
        }

        if (hasPendingPdfLoaders(root)) {
            stablePasses = 0;
        } else {
            stablePasses += 1;
            if (stablePasses >= PDF_CAPTURE_STABLE_PASSES) {
                await waitForNextFrame();
                return root;
            }
        }

        await new Promise((resolve) => setTimeout(resolve, PDF_CAPTURE_POLL_MS));
    }

    const root = contentRef.current;
    if (!root) {
        throw new Error('Content reference is null');
    }

    console.warn(
        'PDF export timed out waiting for portal content to settle:',
        describePendingPdfLoaders(root) || 'unknown loader',
    );

    await waitForNextFrame();
    return root;
};

export const callGeneratePdf = async ({
    contentRef,
    headerLogoDataUrl,
    headerLogoSize,
    headerRightText,
    footerLeftText
}: ServerPdfParams): Promise<Blob> => {
    if (!contentRef.current) {
        throw new Error('Content reference is null');
    }

    try {
        const settledContent = await waitForPdfContentToSettle(contentRef);

        // Get the HTML content
        const htmlContent = settledContent.innerHTML;
        
        // Get computed styles for the content
        const styles = Array.from(document.styleSheets)
            .filter(sheet => {
                try {
                    // Only include same-origin stylesheets
                    return sheet.cssRules !== null;
                } catch (e) {
                    return false;
                }
            })
            .map(sheet => {
                try {
                    return Array.from(sheet.cssRules)
                        .map(rule => rule.cssText)
                        .join('\n');
                } catch (e) {
                    console.warn('Could not access stylesheet:', e);
                    return '';
                }
            })
            .join('\n');

        // Build footer HTML
        const footerLines = Array.isArray(footerLeftText) 
            ? footerLeftText.filter(Boolean) 
            : (footerLeftText ? [footerLeftText] : ['Syncretic']);

        const footerHtml = `
            <div style="width: 100%; font-size: ${PDF_FONT_SIZE_FOOTER}; color: ${PDF_COLOR_TEXT_SECONDARY}; display: flex; justify-content: space-between; align-items: flex-end; padding: ${PDF_FOOTER_PADDING};">
                <div style="text-align: left;">
                    ${footerLines.join('<br>')}
                </div>
                <div style="text-align: right;">
                    <div>${PDF_FOOTER_BRAND_TEXT}</div>
                    <div>${PDF_FOOTER_BRAND_URL}</div>
                    <div class="pageNumber"></div>
                </div>
            </div>
        `;

        // Parse logo size and calculate dynamic top margin
        // Margin = logo height + fixed 10mm padding below header
        const logoSizeMm = headerLogoSize ? parseFloat(headerLogoSize) : LOGO_EXPORT_SIZE_DEFAULT;
        const marginTopMm = logoSizeMm + 10;

        // Build header HTML
        const headerHtml = `
            <div style="width: 100%; font-size: ${PDF_FONT_SIZE_HEADER}; display: flex; justify-content: space-between; align-items: center; padding: ${PDF_HEADER_PADDING};">
                <div style="text-align: left;">
                    ${headerLogoDataUrl ? `<img src="${headerLogoDataUrl}" style="height: ${headerLogoSize || `${LOGO_EXPORT_SIZE_DEFAULT}mm`}; max-height: ${headerLogoSize || `${LOGO_EXPORT_SIZE_DEFAULT}mm`}; object-fit: contain;" />` : ''}
                </div>
                <div style="text-align: right; color: ${PDF_COLOR_TEXT};">
                    ${headerRightText || ''}
                </div>
            </div>
        `;

        // Call Firebase function
        const generatePdf = httpsCallable(functions, 'generatePdf');
        const result = await generatePdf({
            html: htmlContent,
            css: styles,
            headerHtml: headerHtml,
            footerHtml: footerHtml,
            format: PDF_PAGE_FORMAT,
            landscape: PDF_PAGE_LANDSCAPE,
            margin: {
                top: `${marginTopMm}mm`,
                right: PDF_MARGIN_RIGHT,
                bottom: PDF_MARGIN_BOTTOM,
                left: PDF_MARGIN_LEFT
            }
        });

        // Convert base64 to blob
        const pdfData = (result.data as PdfGenerationResponse).pdf;
        const bytes = Uint8Array.from(atob(pdfData), c => c.charCodeAt(0));
        return new Blob([bytes], { type: 'application/pdf' });

    } catch (error) {
        console.error('Server PDF generation failed:', error);
        throw error;
    }
};
